import { ReactElement, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useLatest } from 'react-use'
import { AxiosError } from 'axios'
import { isUndefined, reject, reverse, some, sortBy, toNumber } from 'lodash-es'
import { v4 } from 'uuid'
import { CheckCircle, Close } from '@mui/icons-material'
import { LoadingButton } from '@mui/lab'
import { Alert, Box, Button, MenuItem, Popover, Select, Stack, Typography } from '@mui/material'
import { DataGridPremium } from '@mui/x-data-grid-premium'
import { storeyToLabel } from '@editor/pages/Plans/components/AddPlanDialog/components/utils'
import { wallSettingsOf } from '@editor/pages/StructuralPlanning/components/PlanSettingsForm/utils'
import { ImmutableVector3 } from '@modugen/ui-toolkit'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Label } from '@ui/forms'
import { useSnackbar } from '@ui/snackbar'
import { useModelStore, roofStoreyKey } from '@editorStores'
import { useElementTypes, useGuidToElement } from '@editorHooks'
import {
  getModel,
  getVerticalTransmissionGraph,
  getStiffeningProposal,
  getStiffeningIntervals,
} from '@queries'
import { createBeam, createColumn, createOpening, createVerticalSlab, createWall } from '@mutations'
import { buildErrorMessage } from 'src/constants'

interface Props {
  selectedElements: string[]
  storey?: string
}

const elementTypeToLabel: Record<ElementTypes, string> = {
  vertical_slabs: 'DE',
  beams: 'UZ',
  columns: 'C',
  outer_walls: 'AW',
  inner_walls: 'IW',
  slabs: 'S',
  roof_slabs: 'RS',
  vertical_roof_slabs: 'DA',
  purlins: 'PF',
  rips: 'WS',
  lintels: 'FS',
  slab_beams: 'D',
  roof_slab_beams: 'DA',
  standard_rips: 'WS',
  foundation: 'F',
}

const copyWallRequest = async (
  projectId: string,
  newElement: { wall: unknown; openings: Record<string, unknown>[] },
) => {
  const response = await createWall.request(projectId, newElement.wall)

  const newWallGuid = response.guid

  for (const opening of newElement.openings) {
    await createOpening.request(projectId, {
      ...opening,
      element_guid: newWallGuid,
    })
  }

  return { guid: newWallGuid }
}

type RequestType = (projectId: string, newElement: unknown) => Promise<{ guid: string }>

const elementTypeToRequest: Record<ElementTypes, RequestType | undefined> = {
  vertical_slabs: createVerticalSlab.request,
  beams: createBeam.request,
  columns: createColumn.request,
  outer_walls: copyWallRequest as RequestType,
  inner_walls: copyWallRequest as RequestType,
  slabs: undefined,
  roof_slabs: undefined,
  vertical_roof_slabs: undefined,
  purlins: undefined,
  rips: undefined,
  lintels: undefined,
  slab_beams: undefined,
  roof_slab_beams: undefined,
  standard_rips: undefined,
  foundation: undefined,
}

interface ProcessedElement {
  sourceGuid: string
  targetGuid: string
  targetStorey: string
  success: boolean
}

const CopyToOtherStoreys = ({ selectedElements, storey }: Props): ReactElement => {
  const { projectId } = useParams<{ projectId: string }>()
  const availableStoreys = useModelStore(state => state.availableStoreys)
  const elementTypes = useElementTypes()
  const guidToElement = useGuidToElement()
  const storeyBoundaries = useModelStore(state => state.model.storey_boundaries)
  const queryClient = useQueryClient()

  const { enqueueSnackbar } = useSnackbar()

  const [selectedStoreys, setSelectedStoreys] = useState<string[]>([])
  const targetStoreys = useMemo(
    () =>
      reverse(
        sortBy(
          reject(Array.from(availableStoreys), s => s === storey || s === roofStoreyKey),
          s => toNumber(s),
        ),
      ),
    [availableStoreys, storey],
  )

  const [createdElements, setCreatedElements] = useState<ProcessedElement[]>([])

  const processedElementsRef = useLatest(createdElements)

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)

  const { mutateAsync, isLoading } = useMutation({
    mutationFn: async () => {
      setCreatedElements([])

      for (const selectedStorey of selectedStoreys) {
        const lowerBoundary = storeyBoundaries[selectedStorey][0]
        const upperBoundary = storeyBoundaries[selectedStorey][1]

        for (const elementGuid of selectedElements) {
          const element = guidToElement[elementGuid]

          let newElement: unknown | undefined

          if ('points' in element.shape) {
            if (element.element_type === 'inner_walls' || element.element_type === 'outer_walls') {
              const sourceStorey = element.storey
              const targetStorey = selectedStorey
              const zCoordinateOffset =
                storeyBoundaries[targetStorey][0] - storeyBoundaries[sourceStorey][0]

              newElement = {
                wall: {
                  p1: new ImmutableVector3(element.shape.points[0].x, element.shape.points[0].y, 0),
                  p2: new ImmutableVector3(element.shape.points[1].x, element.shape.points[1].y, 0),
                  placement: element.placement,
                  storey_idx: selectedStorey,
                  guid: v4(),
                  ...wallSettingsOf(element as Wall),
                },
                openings:
                  element.openings?.map(opening => ({
                    shape: {
                      points: opening.shape.points.map(
                        point =>
                          new ImmutableVector3(point.x, point.y, point.z + zCoordinateOffset),
                      ),
                    },
                  })) ?? [],
              }
            } else {
              const newShape = {
                points: element.shape.points.map(
                  point => new ImmutableVector3(point.x, point.y, upperBoundary),
                ),
              }

              newElement = {
                ...element,
                shape: newShape,
                storey: selectedStorey,
                guid: v4(),
              }
              if (element.element_type === 'vertical_slabs') {
                delete (newElement as { label?: string }).label
              }
            }
          } else {
            if (element.element_type === 'beams') {
              const newShape = {
                start: new ImmutableVector3(
                  element.shape.start.x,
                  element.shape.start.y,
                  upperBoundary,
                ),
                end: new ImmutableVector3(element.shape.end.x, element.shape.end.y, upperBoundary),
              }

              newElement = {
                ...element,
                shape: newShape,
                storey: selectedStorey,
                guid: v4(),
              }
              delete (newElement as { label?: string }).label
            } else if (element.element_type === 'columns') {
              const newShape = {
                start: new ImmutableVector3(
                  element.shape.start.x,
                  element.shape.start.y,
                  lowerBoundary,
                ),
                end: new ImmutableVector3(element.shape.end.x, element.shape.end.y, upperBoundary),
              }

              newElement = {
                ...element,
                shape: newShape,
                storey: selectedStorey,
                guid: v4(),
              }
              delete (newElement as { label?: string }).label
            }
          }

          if (isUndefined(newElement)) {
            throw new Error(
              `Could not process shape for element type: ${element.element_type as ElementTypes}`,
            )
          }

          const request = elementTypeToRequest[element.element_type as ElementTypes]
          if (request) {
            try {
              const response = (await request(projectId as string, newElement)) as {
                guid: string
              }
              setCreatedElements(prev => [
                ...prev,
                {
                  sourceGuid: elementGuid,
                  targetGuid: response.guid,
                  targetStorey: selectedStorey,
                  success: true,
                },
              ])
            } catch (error) {
              setCreatedElements(prev => [
                ...prev,
                {
                  sourceGuid: elementGuid,
                  targetGuid: '',
                  targetStorey: selectedStorey,
                  success: false,
                },
              ])
            }
          } else {
            throw new Error(
              `No request found for element type ${element.element_type as ElementTypes}`,
            )
          }
        }
      }
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries(getModel.getKey(projectId)),
        queryClient.invalidateQueries(getVerticalTransmissionGraph.getKey(projectId)),
        queryClient.invalidateQueries(getModel.getKey(projectId)),
        queryClient.invalidateQueries(getModel.getKey(projectId)),
        queryClient.invalidateQueries(getStiffeningProposal.getKey(projectId)),
        queryClient.invalidateQueries(getStiffeningIntervals.getKey(projectId)),
      ])
      if (some(processedElementsRef.current, element => !element.success)) {
        enqueueSnackbar(
          'Manche Elemente konnten nicht kopiert wwerden. Bitte in der Tabelle unten prüfen',
          { variant: 'error' },
        )
      } else {
        enqueueSnackbar(
          'Alle Elemente erfolgreich kopiert. Bitte Aufbauten und andere Konfigurationen prüfen',
          { variant: 'success' },
        )
      }
    },
    onError: async (error: AxiosError) => {
      await Promise.all([
        queryClient.invalidateQueries(getModel.getKey(projectId)),
        queryClient.invalidateQueries(getVerticalTransmissionGraph.getKey(projectId)),
        queryClient.invalidateQueries(getModel.getKey(projectId)),
        queryClient.invalidateQueries(getModel.getKey(projectId)),
        queryClient.invalidateQueries(getStiffeningProposal.getKey(projectId)),
        queryClient.invalidateQueries(getStiffeningIntervals.getKey(projectId)),
      ])
      enqueueSnackbar(
        buildErrorMessage(
          error,
          'Fehler beim Kopieren der Elemente. Ggf. Wurden bereits Elemente angelegt, bitte prüfen',
        ),
        {
          variant: 'error',
        },
      )
    },
  })

  return (
    <>
      <Stack direction="column" spacing={1} width="100%" pt={1}>
        <Typography variant="h6">In anderes Stockwerk kopieren</Typography>

        <Alert severity="info">
          Elemente werden Stück für Stück in die gewählten Stockwerke kopiert. Je mehr Elemente und
          Stockwerke ausgewählt werden, desto länger dauert es.
        </Alert>

        <Label>Ziel-Stockwerke{storey ? ` (aktuell: ${storeyToLabel(storey)})` : ''}</Label>

        <Stack direction="row" spacing={1} alignItems="flex-start">
          <Select
            multiple
            value={selectedStoreys}
            onChange={e => {
              const value = e.target.value
              setSelectedStoreys(typeof value === 'string' ? value.split(',') : value)
            }}
            size="small"
            fullWidth
            displayEmpty
            renderValue={chosen =>
              chosen.length === 0
                ? 'Stockwerke wählen…'
                : targetStoreys
                    .filter(s => chosen.includes(s))
                    .map(storeyToLabel)
                    .join(', ')
            }
            sx={{ maxWidth: '100%', minWidth: 200 }}
          >
            {targetStoreys.map(s => (
              <MenuItem key={s} value={s}>
                {storeyToLabel(s)}
              </MenuItem>
            ))}
          </Select>

          <LoadingButton
            onClick={event => {
              const firstElement = selectedElements[0]
              const element = guidToElement[firstElement]
              if (
                element?.element_type === 'outer_walls' ||
                element?.element_type === 'inner_walls'
              ) {
                setAnchorEl(event.currentTarget)
              } else {
                mutateAsync()
              }
            }}
            variant="contained"
            size="small"
            disabled={isLoading || selectedStoreys.length === 0 || !selectedElements.length}
            loading={isLoading}
          >
            Kopieren
          </LoadingButton>
          <Popover
            open={!!anchorEl}
            anchorEl={anchorEl}
            onClose={() => setAnchorEl(null)}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
          >
            <Stack direction="column" spacing={1} p={2}>
              <Alert severity="warning" sx={{ maxWidth: 500 }}>
                Wände werden inklusive Öffnungen in das ausgewählte Stockwerk kopiert. Falls die
                Öffnungen nicht angelegt werden können, wird die Wand ohne die Öffnung angelegt.
                Bitte genau prüfen und ggf. manuell die Öffnungen anlegen.
              </Alert>

              <Stack direction="row" spacing={1} display="flex" justifyContent="flex-end">
                <Button onClick={() => setAnchorEl(null)}>Schließen</Button>
                <Button
                  onClick={() => {
                    mutateAsync()
                    setAnchorEl(null)
                  }}
                  variant="contained"
                >
                  Anlegen
                </Button>
              </Stack>
            </Stack>
          </Popover>
        </Stack>

        {createdElements && createdElements.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>
              Kopierte Elemente
            </Typography>
            <DataGridPremium
              rows={createdElements.map((element, index) => ({
                id: `${element.sourceGuid}-${element.targetStorey}-${element.targetGuid}-${index}`,
                sourceGuid: element.sourceGuid,
                targetGuid: element.targetGuid,
                targetStorey: element.targetStorey,
                success: element.success,
              }))}
              columns={[
                {
                  field: 'sourceGuid',
                  headerName: 'Quell-GUID',
                  width: 200,
                  flex: 1,
                },
                {
                  field: 'targetStorey',
                  headerName: 'Ziel-Stockwerk',
                  width: 120,
                  flex: 1,
                  valueFormatter: (value: string) => storeyToLabel(value),
                },
                {
                  field: 'elementType',
                  headerName: 'Typ',
                  width: 200,
                  flex: 1,
                  renderCell: params => {
                    // @ts-ignore
                    const elementType = elementTypes[params.row.sourceGuid]
                    return elementType ? elementTypeToLabel[elementType] : elementType
                  },
                },
                {
                  field: 'targetGuid',
                  headerName: 'Erstelltes Element',
                  width: 150,
                  flex: 1,
                },
                {
                  field: 'success',
                  headerName: 'Erfolgreich',
                  width: 150,
                  flex: 1,
                  renderCell: params => (
                    <Box
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                      width="100%"
                      height="100%"
                    >
                      {params.row.success ? (
                        <CheckCircle color="success" />
                      ) : (
                        <Close color="error" />
                      )}
                    </Box>
                  ),
                },
              ]}
              hideFooter
              disableRowSelectionOnClick
              sx={{
                '& .MuiDataGrid-cell': {
                  borderRight: 1,
                  borderColor: 'divider',
                },
              }}
              density="compact"
              onCellClick={params => {
                navigator.clipboard.writeText(params.value as string)
                enqueueSnackbar('Wert in Zwischenablage kopiert', { variant: 'success' })
              }}
            />
          </Box>
        )}
      </Stack>
    </>
  )
}
export default CopyToOtherStoreys
